import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Grid,
} from "@mui/material";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/auth-context";
import LogoLetter from "@/assets/logo.png";
import CardStack from "@/components/CardStack";
import { ACTIVE_COLOR } from "@/constants/color";
import { enqueueSnackbar } from "notistack";

const schema = yup.object().shape({
  email: yup.string().required("Campo obligatorio"),
  password: yup.string().required("Campo obligatorio"),
});

type FormData = {
  email: string;
  password: string;
};

export default function Login() {
  const { login, loading, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [setLoginError] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || "/home";

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated]);

  const onSubmit = async (data: FormData) => {
    try {
      await login(data.email, data.password, from);
    } catch (err) {
      enqueueSnackbar("Credenciales inválidas", { variant: "error" });
      setLoginError(err);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2, // padding horizontal para móviles
      }}
    >
      <Grid
        container
        spacing={2}
        direction="row"
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          size={{ xs: 12, lg: 6 }}
          sx={{
            display: { xs: "none", lg: "block" },
          }}
        >
          <CardStack />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }} sx={{ textAlign: "center" }}>
          <img
            src={LogoLetter}
            alt="logo"
            height={150}
            style={{ marginBottom: 2 }}
          />
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ p: 2 }}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Correo o usuario"
                  margin="normal"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputLabelProps={{ style: { color: "white" } }}
                  FormHelperTextProps={{ style: { color: "white" } }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(8px)",
                    borderRadius: 2,
                    "& .MuiInputBase-root": {
                      color: "white",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "white",
                      },
                      "&:hover fieldset": {
                        borderColor: "white",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={20} color="white" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputLabelProps={{ style: { color: "white" } }}
                  FormHelperTextProps={{ style: { color: "white" } }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)", // blanco translúcido
                    backdropFilter: "blur(8px)", // efecto borroso
                    borderRadius: 2,
                    "& .MuiInputBase-root": {
                      color: "white",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "white",
                      },
                      "&:hover fieldset": {
                        borderColor: "white",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock size={20} color="white" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                        >
                          {showPassword ? (
                            <EyeOff size={20} color="white" />
                          ) : (
                            <Eye size={20} color="white" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 3,
                fontWeight: "bold",
                backgroundColor: ACTIVE_COLOR,
                "&:hover": {
                  backgroundColor: ACTIVE_COLOR,
                },
              }}
            >
              {loading ? "Ingresando..." : "Entrar"}
            </Button>
            <Typography
              variant="body2"
              align="center"
              mt={2}
              sx={{ color: "#e3e3e3" }}
            >
              <Link
                to="/forgot-password"
                style={{ color: "#fff", textDecoration: "none" }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
